/**
 * ImageEditor Component
 *
 * Image editing interface with multiple options:
 * 1. Upload - Upload a new image from computer
 * 2. Media Library - Select from website's media library
 * 3. Regenerate - Search with original query for alternatives
 * 4. Edit Prompt - Modify search query and search
 * 5. Browse Pexels - Free-form Pexels exploration
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { EditableElement } from '../types';
import { useRenderer } from '../context';

// ============================================================================
// Types
// ============================================================================

interface ImageEditorProps {
  element: EditableElement;
  websiteId: string;
  onClose: () => void;
}

type EditorTab = 'upload' | 'library' | 'browse';

interface PexelsPhoto {
  id: number;
  width: number;
  height: number;
  photographer: string;
  alt: string;
  avg_color: string;
  src: {
    small: string;
    medium: string;
    large: string;
  };
}

interface MediaItem {
  id: string;
  url: string;
  thumbnailUrl: string | null;
  alt_text: string | null;
  original_filename: string | null;
  original_query: string | null;
  source: string;
  width: number | null;
  height: number | null;
}

// ============================================================================
// API Functions
// ============================================================================

const API_BASE = import.meta.env.VITE_API_URL || 'https://niftee-orchestrator.jason-a6e.workers.dev';

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  // Get token from niftee_session (same as AuthContext)
  let token: string | null = null;
  try {
    const stored = localStorage.getItem('niftee_session');
    if (stored) {
      const parsed = JSON.parse(stored);
      token = parsed.token || null;
    }
  } catch {
    // Ignore parse errors
  }

  const headers: HeadersInit = {
    ...options.headers,
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(`${API_BASE}${url}`, { ...options, headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || error.message || 'Request failed');
  }
  return response.json();
}

// ============================================================================
// Tab Button Component
// ============================================================================

interface TabButtonProps {
  tab: EditorTab;
  activeTab: EditorTab;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

function TabButton({ tab, activeTab, label, icon, onClick }: TabButtonProps) {
  const isActive = tab === activeTab;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
        isActive
          ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-200'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// ============================================================================
// Upload Tab
// ============================================================================

interface UploadTabProps {
  websiteId: string;
  onSelect: (url: string) => void;
}

function UploadTab({ websiteId, onSelect }: UploadTabProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File must be less than 10MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await fetchWithAuth(`/api/websites/${websiteId}/media/upload`, {
        method: 'POST',
        body: formData,
      });

      onSelect(result.media.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <div className="space-y-4">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
          dragOver
            ? 'border-primary-400 bg-primary-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <svg className="w-10 h-10 text-primary-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-600">
              <span className="text-primary-600 font-medium">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-gray-400">PNG, JPG, WebP or GIF up to 10MB</p>
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Media Library Tab
// ============================================================================

interface LibraryTabProps {
  websiteId: string;
  onSelect: (url: string) => void;
}

function LibraryTab({ websiteId, onSelect }: LibraryTabProps) {
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load media on mount
  useEffect(() => {
    fetchWithAuth(`/api/websites/${websiteId}/media`)
      .then((data) => {
        setMedia(data.media);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [websiteId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="w-8 h-8 text-primary-500 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm">
        {error}
      </div>
    );
  }

  if (media.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-gray-500">No images in your library yet</p>
        <p className="text-sm text-gray-400 mt-1">Upload an image or save from Pexels to get started</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3 max-h-[400px] overflow-y-auto p-1">
      {media.map((item) => (
        <button
          key={item.id}
          onClick={() => onSelect(item.url)}
          className="group relative h-[120px] rounded-lg overflow-hidden border-2 border-transparent hover:border-primary-500 transition-all"
        >
          <img
            src={item.thumbnailUrl || item.url}
            alt={item.alt_text || 'Media'}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <span className="text-white text-sm font-medium">Select</span>
          </div>
        </button>
      ))}
    </div>
  );
}

// ============================================================================
// Pexels Search Tab (used by Regenerate, Prompt, and Browse)
// ============================================================================

interface PexelsSearchTabProps {
  websiteId: string;
  initialQuery?: string;
  onSelect: (url: string, photo: PexelsPhoto) => void;
  showQueryInput?: boolean;
  autoSearch?: boolean;
}

function PexelsSearchTab({
  websiteId,
  initialQuery = '',
  onSelect,
  showQueryInput = true,
  autoSearch = false,
}: PexelsSearchTabProps) {
  const [query, setQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(autoSearch);
  const [photos, setPhotos] = useState<PexelsPhoto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<number | null>(null);

  const handleSearch = useCallback(async (searchQuery?: string) => {
    const q = searchQuery ?? query;
    if (!q.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchWithAuth(`/api/websites/${websiteId}/media/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q, perPage: 20 }),
      });
      setPhotos(result.photos);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [websiteId, query]);

  // Auto-search on mount if needed
  useEffect(() => {
    if (autoSearch && initialQuery) {
      handleSearch(initialQuery);
    }
  }, [autoSearch, initialQuery, handleSearch]);

  const handleSelectPhoto = async (photo: PexelsPhoto) => {
    setSaving(photo.id);
    setError(null);

    try {
      // Save to media library
      const result = await fetchWithAuth(`/api/websites/${websiteId}/media/save-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          photo: {
            ...photo,
            src: {
              ...photo.src,
              original: photo.src.large,
              large2x: photo.src.large,
            },
          },
          originalQuery: query,
        }),
      });

      onSelect(result.media.url, photo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save image');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-4">
      {showQueryInput && (
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for images..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            type="button"
            onClick={() => handleSearch()}
            disabled={loading || !query.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              'Search'
            )}
          </button>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading && photos.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <svg className="w-8 h-8 text-primary-500 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      ) : photos.length > 0 ? (
        <div className="grid grid-cols-3 gap-3 max-h-[400px] overflow-y-auto p-1">
          {photos.map((photo) => (
            <button
              key={photo.id}
              onClick={() => handleSelectPhoto(photo)}
              disabled={saving !== null}
              className="group relative rounded-lg overflow-hidden border-2 border-transparent hover:border-primary-500 transition-all disabled:opacity-50 h-[120px]"
            >
              <img
                src={photo.src.medium}
                alt={photo.alt || 'Stock photo'}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {saving === photo.id ? (
                  <svg className="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <span className="text-white text-sm font-medium">Select</span>
                )}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-white text-xs truncate">by {photo.photographer}</p>
              </div>
            </button>
          ))}
        </div>
      ) : !loading && query ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No images found</p>
          <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
        </div>
      ) : !showQueryInput ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Searching for alternatives...</p>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-gray-500">Search for free stock photos</p>
          <p className="text-sm text-gray-400 mt-1">Powered by Pexels</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main ImageEditor Component
// ============================================================================

export function ImageEditor({ element, websiteId, onClose }: ImageEditorProps) {
  const { stageChange, getValue, revertChange, stagedChanges } = useRenderer();

  // Get current value
  const currentValue = getValue(element.id, element.value);
  const currentUrl = Array.isArray(currentValue) ? currentValue[0] : currentValue;

  // State
  const [activeTab, setActiveTab] = useState<EditorTab>('upload');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const hasUnsavedChanges = element.id in stagedChanges;


  // Handle image selection from any tab
  const handleImageSelect = useCallback((url: string) => {
    setPreviewUrl(url);
  }, []);

  // Save changes
  const handleSave = useCallback(() => {
    if (previewUrl) {
      stageChange(element.id, element.value, previewUrl);
    }
    onClose();
  }, [element, stageChange, previewUrl, onClose]);

  // Revert
  const handleRevert = useCallback(() => {
    setPreviewUrl(null);
    if (hasUnsavedChanges) {
      revertChange(element.id);
    }
  }, [hasUnsavedChanges, revertChange]);

  const displayUrl = previewUrl || currentUrl;
  const hasChanges = previewUrl !== null;

  return (
    <div className="space-y-6">
      {/* Current / Preview Image */}
      <div className="relative">
        <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
          <img
            src={displayUrl}
            alt={element.label}
            className="w-full h-full object-cover"
          />
        </div>
        {previewUrl && (
          <div className="absolute top-2 right-2 px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-medium rounded">
            Preview
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2">
        <TabButton
          tab="upload"
          activeTab={activeTab}
          label="Upload"
          onClick={() => setActiveTab('upload')}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          }
        />
        <TabButton
          tab="library"
          activeTab={activeTab}
          label="Media Library"
          onClick={() => setActiveTab('library')}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
        />
        <TabButton
          tab="browse"
          activeTab={activeTab}
          label="Browse Pexels"
          onClick={() => setActiveTab('browse')}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          }
        />
      </div>

      {/* Tab Content */}
      <div className="min-h-[200px]">
        {activeTab === 'upload' && (
          <UploadTab websiteId={websiteId} onSelect={handleImageSelect} />
        )}
        {activeTab === 'library' && (
          <LibraryTab websiteId={websiteId} onSelect={handleImageSelect} />
        )}
        {activeTab === 'browse' && (
          <PexelsSearchTab
            websiteId={websiteId}
            onSelect={(url) => handleImageSelect(url)}
            showQueryInput={true}
            autoSearch={false}
          />
        )}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div>
          {(hasChanges || hasUnsavedChanges) && (
            <button
              type="button"
              onClick={handleRevert}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Revert to original
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges && !hasUnsavedChanges}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              hasChanges || hasUnsavedChanges
                ? 'bg-primary-600 hover:bg-primary-700'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
